import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FlatpickrDateField, segmentedPillTone, SEGMENTED_SEMANTIC_COLOR } from './FieldRenderer';
import { useToast } from '../hooks/useToast';
import { evidenceApi } from '../services/api';
import type { FieldValues } from '../types/models';

interface SubmissionLike {
  submissionId: string;
  submittedAt: string;
  status: string;
}

interface Props {
  fieldValues: FieldValues;
  onChange: (fieldId: string, value: any) => void;
  isEditing: boolean;
  evidenceId: string;
  submissions?: SubmissionLike[];
  /** Render as a sub-group inside a caller-owned card (eyebrow label + Log Collection in one
   * row, no card chrome or accent stripe of its own). Default false = unchanged for every
   * other caller. */
  embedded?: boolean;
}

const FREQUENCY_OPTIONS = ['Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'Bi-annually', 'Annually'];
const STATUS_OPTIONS = ['On Track', 'Due Soon', 'Overdue', 'Not Scheduled'];

const daysBetween = (dateStr: string): number | null => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const FREQ_DAYS: Record<string, number> = { Weekly: 7, 'Bi-weekly': 14 };
const FREQ_MONTHS: Record<string, number> = { Monthly: 1, Quarterly: 3, 'Bi-annually': 6, Annually: 12 };

// Last day-of-month (0-indexed month), for clamping e.g. Jan 31 + 1 month -> Feb 28, not Mar 3.
const daysInMonth = (year: number, month0: number): number => new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();

// UTC-only arithmetic throughout - new Date(dateStr) parses a date-only string as UTC midnight,
// but setDate()/setMonth() operate in LOCAL time, so mixing them silently drifts the result by a
// day across a DST transition. Never use the local-time getters/setters on a parsed date-only string.
const addInterval = (fromDateStr: string, frequency: string): string => {
  const base = fromDateStr && !isNaN(new Date(fromDateStr).getTime()) ? fromDateStr : todayIso();
  const [y, m, d] = base.split('-').map(Number);
  if (FREQ_DAYS[frequency]) {
    return new Date(Date.UTC(y, m - 1, d) + FREQ_DAYS[frequency] * 86400000).toISOString().slice(0, 10);
  }
  if (FREQ_MONTHS[frequency]) {
    const totalMonths = m - 1 + FREQ_MONTHS[frequency];
    const year = y + Math.floor(totalMonths / 12);
    const month0 = ((totalMonths % 12) + 12) % 12;
    const day = Math.min(d, daysInMonth(year, month0));
    return new Date(Date.UTC(year, month0, day)).toISOString().slice(0, 10);
  }
  return base;
};

const computeScheduleStatus = (nextDueStr: string): string => {
  if (!nextDueStr) return 'Not Scheduled';
  const days = daysBetween(nextDueStr);
  if (days === null) return 'Not Scheduled';
  if (days < 0) return 'Overdue';
  if (days <= 7) return 'Due Soon';
  return 'On Track';
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const addDays = (dateStr: string, days: number): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d) + days * 86400000).toISOString().slice(0, 10);
};

const midpoint = (aStr: string, bStr: string): string => {
  const a = new Date(aStr).getTime();
  const b = new Date(bStr).getTime();
  return new Date((a + b) / 2).toISOString().slice(0, 10);
};

interface HistoryEntry {
  period: string;
  date?: string;
  status: string;
  note?: string;
}

const TOLERANCE_DAYS = 3;

interface Period {
  expectedDate: string;
  windowStart: string;
  windowEnd: string;
}

// Fixed calendar: periods are pure anchor + frequency math, recomputed on every render -
// never stored, never shifted by lateness. Capped to the most recent 12 for display.
const generatePeriods = (anchor: string, frequency: string, today: string): Period[] => {
  if (!anchor || !frequency) return [];
  const periods: Period[] = [];
  let current = anchor;
  for (let i = 0; i < 500; i++) {
    periods.push({ expectedDate: current, windowStart: addDays(current, -TOLERANCE_DAYS), windowEnd: addDays(current, TOLERANCE_DAYS) });
    if (current >= today) break;
    const next = addInterval(current, frequency);
    if (next <= current) break;
    current = next;
  }
  return periods.slice(-12);
};

const REVIEW_COLOR: Record<string, string> = {
  Accepted: 'success',
  Rejected: 'danger',
  'Clarification Needed': 'warning',
  'Pending Review': 'info',
  Logged: 'secondary',
};

const TIMING_COLOR: Record<string, string> = {
  'On Time': 'success',
  Late: 'warning',
  Missed: 'danger',
  Open: 'info',
  Upcoming: 'secondary',
};

interface PeriodResult extends Period {
  timing: 'Upcoming' | 'Open' | 'On Time' | 'Late' | 'Missed';
  review: string | null;
}

// Nearest-neighbour match: each period "claims" the date range closer to it than to its
// neighbours (the midpoint between adjacent expected dates), so a very late submission reads
// as late for the period it's actually closest to rather than backfilling an old gap.
const matchPeriods = (periods: Period[], submissions: SubmissionLike[], rawHistory: HistoryEntry[], today: string): PeriodResult[] => {
  type Ev = { date: string; submission?: SubmissionLike; log?: HistoryEntry };
  const events: Ev[] = [
    ...submissions.map((s) => ({ date: s.submittedAt.slice(0, 10), submission: s })),
    ...rawHistory.filter((h) => h.status === 'Done').map((h) => ({ date: h.date || `${h.period}-15`, log: h })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return periods.map((p, i) => {
    const zoneStart = i > 0 ? midpoint(periods[i - 1].expectedDate, p.expectedDate) : '0000-01-01';
    const zoneEnd = i < periods.length - 1 ? midpoint(p.expectedDate, periods[i + 1].expectedDate) : '9999-12-31';
    const candidates = events.filter((e) => e.date >= zoneStart && e.date < zoneEnd);
    const chosen = candidates.length > 0 ? candidates[candidates.length - 1] : null;

    let timing: PeriodResult['timing'];
    let review: string | null = null;

    if (!chosen) {
      timing = p.windowEnd < today ? 'Missed' : p.windowStart > today ? 'Upcoming' : 'Open';
    } else {
      timing = chosen.date <= p.windowEnd ? 'On Time' : 'Late';
      if (chosen.submission) {
        review = chosen.submission.status === 'Submitted' || chosen.submission.status === 'In Review' ? 'Pending Review' : chosen.submission.status;
      } else {
        review = 'Logged';
      }
    }

    return { ...p, timing, review };
  });
};

const TIMING_ICON: Record<string, string> = {
  'On Time': 'ri-checkbox-circle-line',
  Late: 'ri-time-line',
  Missed: 'ri-alarm-warning-line',
  Open: 'ri-hourglass-line',
  Upcoming: 'ri-calendar-line',
};

// bg-label-secondary's np- equivalent is the neutral chip tone, not a "secondary" one.
const toTone = (color: string): string => (color === 'secondary' ? 'neutral' : color);

const periodAccent = (p?: PeriodResult): string => {
  if (!p) return 'secondary';
  if (p.timing === 'Missed' || p.review === 'Rejected') return 'danger';
  if (p.timing === 'Late' || p.review === 'Clarification Needed') return 'warning';
  if (p.review === 'Accepted' || p.timing === 'On Time') return 'success';
  return 'secondary';
};

const PillRow: React.FC<{
  label: string;
  fieldId: string;
  options: string[];
  value: string;
  isEditing: boolean;
  onChange: (fieldId: string, value: any) => void;
  displayValue?: (value: string) => string;
}> = ({ label, fieldId, options, value, isEditing, onChange, displayValue }) => {
  if (!isEditing) {
    const color = SEGMENTED_SEMANTIC_COLOR[value.toLowerCase()];
    const shown = value ? (displayValue ? displayValue(value) : value) : '—';
    return (
      <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
        <span className="np-sub np-mb-0">{label}</span>
        {color ? (
          <span className={`np-chip np-tone-${color === 'secondary' ? 'neutral' : color}`}>{shown}</span>
        ) : (
          <span style={{ fontWeight: 600 }}>{shown}</span>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 border-bottom">
      <span className="np-sub np-mb-0">{label}</span>
      <div className="np-pill-toggle-group" role="group">
        {options.map((option) => {
          const isActive = option === value;
          return (
            <button
              key={option}
              type="button"
              className={`np-pill-toggle np-tone-${segmentedPillTone(option)}${isActive ? ' active' : ''}`}
              onClick={() => onChange(fieldId, option)}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CollectionScheduleCard: React.FC<Props> = ({ fieldValues, onChange, isEditing, evidenceId, submissions = [], embedded = false }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [showLogForm, setShowLogForm] = useState(false);
  const [collectedOn, setCollectedOn] = useState(todayIso());
  const [outcome, setOutcome] = useState<'Done' | 'Missed'>('Done');
  const [note, setNote] = useState('');

  const frequency = String(fieldValues['collection_frequency'] || '');
  const scheduleStartDate = String(fieldValues['schedule_start_date'] || '');
  const lastReviewed = String(fieldValues['evidence_last_reviewed'] || '');
  const nextDue = String(fieldValues['next_due_date'] || '');
  const status = String(fieldValues['schedule_status'] || '');
  const comment = String(fieldValues['frequency_comment'] || '');

  const rawHistory: HistoryEntry[] = Array.isArray(fieldValues['collection_history'])
    ? (fieldValues['collection_history'] as HistoryEntry[])
    : [];

  const latestSubmission = submissions[0];
  const awaitingReview = !!latestSubmission && latestSubmission.status !== 'Accepted' && latestSubmission.status !== 'Rejected';

  const periods = generatePeriods(scheduleStartDate, frequency, todayIso());
  const periodResults = matchPeriods(periods, submissions, rawHistory, todayIso());
  const currentPeriod = periodResults[periodResults.length - 1];
  const pastPeriods = periodResults.slice(0, -1).reverse();

  const logMutation = useMutation({
    mutationFn: () => {
      const period = collectedOn.slice(0, 7);
      const newEntry: HistoryEntry = { period, date: collectedOn, status: outcome, note: note.trim() || undefined };
      const newHistory = [...rawHistory.filter((h) => h.period !== period), newEntry];
      const newNextDue = frequency ? addInterval(collectedOn, frequency) : nextDue;
      const newStatus = frequency ? computeScheduleStatus(newNextDue) : status;
      const patch: Record<string, any> = {
        collection_history: newHistory,
        evidence_last_reviewed: collectedOn,
        next_due_date: newNextDue,
        schedule_status: newStatus,
      };
      return evidenceApi.update(evidenceId, { fields: { ...fieldValues, ...patch } }).then(() => patch);
    },
    onSuccess: (patch) => {
      Object.entries(patch).forEach(([fieldId, value]) => onChange(fieldId, value));
      queryClient.invalidateQueries({ queryKey: ['evidence', evidenceId] });
      toast.success('Collection logged.');
      setShowLogForm(false);
      setNote('');
      setOutcome('Done');
      setCollectedOn(todayIso());
    },
    onError: (e: any) => toast.error(e.message || 'Failed to log collection'),
  });

  const heroAccent = awaitingReview
    ? 'info'
    : !frequency || !scheduleStartDate
    ? 'secondary'
    : periodAccent(currentPeriod);

  const logButton = !showLogForm && evidenceId && (
    <button
      type="button"
      className={`np-btn ${embedded ? 'np-btn-ghost' : 'np-btn-primary'} np-btn-sm${embedded ? ' ms-auto' : ''}`}
      onClick={() => setShowLogForm(true)}
    >
      <i className="ri-checkbox-circle-line"></i>Log Collection
    </button>
  );

  const body = (
    <>
          {awaitingReview ? (
            <div className="d-flex align-items-center gap-3 mb-3">
              <span className="np-icon-badge np-tone-info flex-shrink-0">
                <i className="ri-time-line"></i>
              </span>
              <div>
                <p className="mb-0 fw-medium">Awaiting review</p>
                <small className="np-hint">
                  A submission is in and waiting on a reviewer — the due date will update automatically once it's accepted.
                </small>
              </div>
            </div>
          ) : !frequency ? (
            <div className="d-flex align-items-center gap-3">
              <span className="np-icon-badge np-tone-neutral flex-shrink-0">
                <i className="ri-calendar-line"></i>
              </span>
              <div>
                <p className="mb-0 fw-medium">Not on a recurring schedule</p>
                <small className="np-hint">Set a Collection Frequency below to start tracking this evidence's due dates.</small>
              </div>
            </div>
          ) : !scheduleStartDate ? (
            <div className="d-flex align-items-center gap-3">
              <span className="np-icon-badge np-tone-neutral flex-shrink-0">
                <i className="ri-flag-line"></i>
              </span>
              <div>
                <p className="mb-0 fw-medium">Schedule Start Date not set</p>
                <small className="np-hint">
                  Set a Schedule Start Date below to generate the fixed collection calendar for this evidence.
                </small>
              </div>
            </div>
          ) : currentPeriod ? (
            <div className="d-flex align-items-start gap-3 mb-3">
              <span className={`np-icon-badge np-tone-${toTone(heroAccent)} flex-shrink-0`}>
                <i className={TIMING_ICON[currentPeriod.timing]}></i>
              </span>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <p className="mb-1 np-sub">Current period · expected {formatDate(currentPeriod.expectedDate)}</p>
                <h4 className="mb-1 fw-bold">{currentPeriod.timing}</h4>
                <div className="d-flex flex-wrap gap-2">
                  <span className={`np-chip np-tone-${toTone(TIMING_COLOR[currentPeriod.timing])}`}>{currentPeriod.timing}</span>
                  {currentPeriod.review && (
                    <span className={`np-chip np-tone-${toTone(REVIEW_COLOR[currentPeriod.review] || 'secondary')}`}>
                      {currentPeriod.review}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {pastPeriods.length > 0 && (
            <div className="mb-3">
              <p
                className="np-sub mb-2"
                style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                Collection history
              </p>
              <div className="np-list">
                {pastPeriods.map((p, i) => (
                  <div key={i} className="np-list-item flex-wrap">
                    <span className="fw-medium small np-tabular">{formatDate(p.expectedDate)}</span>
                    <div className="d-flex gap-1">
                      <span className={`np-chip np-tone-${toTone(TIMING_COLOR[p.timing])}`}>
                        {p.timing}
                      </span>
                      {p.review && (
                        <span className={`np-chip np-tone-${toTone(REVIEW_COLOR[p.review] || 'secondary')}`}>
                          {p.review}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showLogForm && (
            <div className="border rounded p-3 mb-3" style={{ background: 'var(--np-surface-2)' }}>
              <div className="row g-2 align-items-end">
                <div className="col-sm-4 np-field mb-0">
                  <label>Collected On</label>
                  <FlatpickrDateField
                    inputId="log_collected_on"
                    label="Collected On"
                    required={false}
                    value={collectedOn}
                    readOnly={false}
                    hideLabel
                    onChange={(v) => setCollectedOn(v)}
                  />
                </div>
                <div className="col-sm-4 np-field mb-0">
                  <label>Outcome</label>
                  <select
                    className="np-select"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value as 'Done' | 'Missed')}
                  >
                    <option value="Done">Done</option>
                    <option value="Missed">Missed</option>
                  </select>
                </div>
              </div>
              <div className="np-field mt-2 mb-0">
                <label>Note (optional)</label>
                <textarea
                  className="np-input"
                  rows={2}
                  placeholder="e.g. evidence attached to ticket SEC-4021…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <div className="d-flex gap-2 mt-3">
                <button
                  type="button"
                  className="np-btn np-btn-success np-btn-sm"
                  disabled={logMutation.isPending}
                  onClick={() => logMutation.mutate()}
                >
                  {logMutation.isPending ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <i className="ri-save-line"></i>
                  )}
                  Save
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-ghost np-btn-sm"
                  disabled={logMutation.isPending}
                  onClick={() => setShowLogForm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="mt-2">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 border-bottom">
              <span className="np-sub np-mb-0">Schedule Start Date</span>
              {isEditing ? (
                <div style={{ minWidth: 160 }}>
                  <FlatpickrDateField
                    inputId="field_schedule_start_date"
                    label="Schedule Start Date"
                    required={false}
                    value={scheduleStartDate}
                    readOnly={false}
                    hideLabel
                    onChange={(v) => onChange('schedule_start_date', v)}
                  />
                </div>
              ) : (
                <span className="fw-medium np-tabular">{scheduleStartDate ? formatDate(scheduleStartDate) : '—'}</span>
              )}
            </div>

            <PillRow
              label="Collection Frequency"
              fieldId="collection_frequency"
              options={FREQUENCY_OPTIONS}
              value={frequency}
              isEditing={isEditing}
              onChange={onChange}
            />

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 border-bottom">
              <span className="np-sub np-mb-0">Last Reviewed</span>
              {isEditing ? (
                <div style={{ minWidth: 160 }}>
                  <FlatpickrDateField
                    inputId="field_evidence_last_reviewed"
                    label="Last Reviewed"
                    required={false}
                    value={lastReviewed}
                    readOnly={false}
                    hideLabel
                    onChange={(v) => onChange('evidence_last_reviewed', v)}
                  />
                </div>
              ) : (
                <span className="fw-medium np-tabular">{lastReviewed ? formatDate(lastReviewed) : '—'}</span>
              )}
            </div>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 border-bottom">
              <span className="np-sub np-mb-0">Next Due Date</span>
              {isEditing ? (
                <div style={{ minWidth: 160 }}>
                  <FlatpickrDateField
                    inputId="field_next_due_date"
                    label="Next Due Date"
                    required={false}
                    value={nextDue}
                    readOnly={false}
                    hideLabel
                    onChange={(v) => onChange('next_due_date', v)}
                  />
                </div>
              ) : (
                <span className="fw-medium np-tabular">{nextDue ? formatDate(nextDue) : '—'}</span>
              )}
            </div>

            <PillRow
              label="Schedule Status"
              fieldId="schedule_status"
              options={STATUS_OPTIONS}
              value={status}
              isEditing={isEditing}
              onChange={onChange}
            />
          </div>

          <div className="np-field mt-3 mb-0">
            <label>Frequency Comment</label>
            {isEditing ? (
              <textarea
                className="np-input"
                rows={2}
                value={comment}
                onChange={(e) => onChange('frequency_comment', e.target.value)}
              />
            ) : (
              <p className="mb-0">{comment || <span className="np-hint">—</span>}</p>
            )}
          </div>
    </>
  );

  if (embedded) {
    return (
      <div className="np-details-group">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="eyebrow-label mb-0">Collection Schedule</span>
          {logButton}
        </div>
        {body}
      </div>
    );
  }

  return (
    <div className="col-12 mb-4">
      <div className={`np-card np-card-accent-${toTone(heroAccent)}`}>
        <div className="np-card-head">
          <h2>Collection Schedule</h2>
          {logButton}
        </div>
        <div className="np-card-body">{body}</div>
      </div>
    </div>
  );
};
